import { BaseApiRequest } from './BaseApi';
import { toast } from 'react-toastify';
import { logDebug, logWarn } from './Logging';
import { isMissingTokenError } from './apiErrors';
import download from 'downloadjs';

/**
 * POST /ScheduledReport/schedule
 * Body: CreateScheduledReportRequest
 * Response 201: ScheduledReportResponse
 */
export const scheduleReport = (signal, body) => {
  return BaseApiRequest(signal, '/ScheduledReport/schedule', 'gpr', 'POST', body)
    .then((data) => {
      toast.success('Report scheduled successfully');
      return data;
    })
    .catch((err) => {
      const errorMessage = err?.errors?.[0] || 'Failed to schedule report';
      logWarn(errorMessage, err);
      if (!isMissingTokenError(err)) toast.error(errorMessage);
      return Promise.reject(err);
    });
};

/**
 * POST /ScheduledReport/send-now
 * Body: CreateScheduledReportRequest
 * Response 200: ScheduledReportResponse
 */
export const sendReportNow = (signal, body) => {
  return BaseApiRequest(signal, '/ScheduledReport/send-now', 'gpr', 'POST', body)
    .then((data) => {
      toast.success('Immediate report request sent successfully');
      return data;
    })
    .catch((err) => {
      const errorMessage = err?.errors?.[0] || 'Failed to send report';
      logWarn(errorMessage, err);
      if (!isMissingTokenError(err)) toast.error(errorMessage);
      return Promise.reject(err);
    });
};

/**
 * POST /ScheduledReport/send-now (with download)
 * Sends a report immediately and downloads the returned PDF.
 */
export const sendAndDownloadReport = (signal, body) => {
  return BaseApiRequest(signal, '/ScheduledReport/send-now', 'gpr', 'POST', body, {}, true)
    .then((respBlob) => {
      download(respBlob.blob, respBlob.fileName || `${body.reportName || 'report'}.pdf`, 'application/pdf');
      toast.success('Download complete');
    })
    .catch((err) => {
      const errorMessage = err?.errors?.[0] || 'Failed to send report';
      logWarn(errorMessage, err);
      if (!isMissingTokenError(err)) toast.error(errorMessage);
      return Promise.reject(err);
    });
};

/**
 * GET /ScheduledReport/myrequests
 * Response 200: ScheduledReportResponse[]
 */
export const getMyReportRequests = (signal) => {
  return BaseApiRequest(signal, '/ScheduledReport/myrequests', 'gpr', 'GET')
    .catch((err) => {
      const errorMessage = 'Failed to retrieve report requests';
      logWarn(errorMessage, err);
      if (!isMissingTokenError(err)) toast.error(errorMessage);
      return [];
    });
};

/**
 * GET /ScheduledReport/{reportRequestId}/history
 * Response 200: ScheduledReportExecutionResponse[]
 */
export const getReportHistory = (signal, reportRequestId) => {
  return BaseApiRequest(
    signal,
    `/ScheduledReport/${encodeURIComponent(reportRequestId)}/history`,
    'gpr',
    'GET'
  ).catch((err) => {
    if (err?.name === 'AbortError') {
      logDebug('Report history request aborted by user.', {
        reportRequestId
      });
      return [];
    }
    const errorMessage = 'Failed to retrieve report history';
    logWarn(errorMessage, err);
    if (!isMissingTokenError(err)) toast.error(errorMessage);
    return [];
  });
};

/**
 * GET /ScheduledReport/jobs/{jobId}
 * Response 200: ScheduledReportJobResponse
 */
export const getJobStatus = (signal, jobId) => {
  return BaseApiRequest(
    signal,
    `/ScheduledReport/jobs/${encodeURIComponent(jobId)}`,
    'gpr',
    'GET'
  ).catch((err) => {
    const errorMessage = 'Failed to retrieve job status';
    logWarn(errorMessage, err);
    if (!isMissingTokenError(err)) toast.error(errorMessage);
    return null;
  });
};

/**
 * PUT /ScheduledReport/{reportRequestId}
 * Body: UpdateScheduledReportRequest
 * Response 200: ScheduledReportResponse
 */
export const updateReport = (signal, reportRequestId, body) => {
  return BaseApiRequest(
    signal,
    `/ScheduledReport/${encodeURIComponent(reportRequestId)}`,
    'gpr',
    'PUT',
    body
  )
    .then((data) => {
      toast.success('Report updated successfully');
      return data;
    })
    .catch((err) => {
      const errorMessage = err?.errors?.[0] || 'Failed to update report';
      logWarn(errorMessage, err);
      if (!isMissingTokenError(err)) toast.error(errorMessage);
      return Promise.reject(err);
    });
};

/**
 * PATCH /ScheduledReport/{reportRequestId}/active
 * Body: { isActive: boolean }
 * Response 200: ScheduledReportResponse
 */
export const setReportActiveStatus = (signal, reportRequestId, isActive) => {
  return BaseApiRequest(
    signal,
    `/ScheduledReport/${encodeURIComponent(reportRequestId)}/active`,
    'gpr',
    'PATCH',
    { isActive }
  )
    .then((data) => {
      toast.success(isActive ? 'Report activated' : 'Report deactivated');
      return data;
    })
    .catch((err) => {
      const errorMessage = err?.errors?.[0] || 'Failed to update report status';
      logWarn(errorMessage, err);
      if (!isMissingTokenError(err)) toast.error(errorMessage);
      return Promise.reject(err);
    });
};

/**
 * PATCH /ScheduledReport/{reportRequestId}/archive
 * Response 200: ScheduledReportResponse
 */
export const archiveReport = (signal, reportRequestId) => {
  return BaseApiRequest(
    signal,
    `/ScheduledReport/${encodeURIComponent(reportRequestId)}/archive`,
    'gpr',
    'PATCH'
  )
    .then((data) => {
      toast.success('Report archived successfully');
      return data;
    })
    .catch((err) => {
      const errorMessage = err?.errors?.[0] || 'Failed to archive report';
      logWarn(errorMessage, err);
      if (!isMissingTokenError(err)) toast.error(errorMessage);
      return Promise.reject(err);
    });
};

/**
 * GET /ScheduledReport/myexecutions
 * Response 200: ScheduledReportExecutionResponse[]
 */
export const getAllExecutions = (signal) => {
  return BaseApiRequest(signal, '/ScheduledReport/myexecutions', 'gpr', 'GET').catch((err) => {
    const errorMessage = 'Failed to retrieve executions';
    logWarn(errorMessage, err);
    if (!isMissingTokenError(err)) toast.error(errorMessage);
    return [];
  });
};

/**
 * GET /ScheduledReport/myrequests/archived
 * Response 200: ScheduledReportResponse[]
 */
export const getArchivedReports = (signal) => {
  return BaseApiRequest(signal, '/ScheduledReport/myrequests/archived', 'gpr', 'GET').catch((err) => {
    const errorMessage = 'Failed to retrieve archived reports';
    logWarn(errorMessage, err);
    if (!isMissingTokenError(err)) toast.error(errorMessage);
    return [];
  });
};

/**
 * GET /ScheduledReport/executions/{executionId}/report/{reportId}
 * Downloads the generated report file
 */
export const downloadExecutionReport = async (signal, executionId, reportId, defaultFileName) => {
  return BaseApiRequest(
    signal, 
    `/ScheduledReport/executions/${encodeURIComponent(executionId)}/report/${encodeURIComponent(reportId)}`, 
    'gpr', 
    'GET', 
    undefined,
    {}, 
    true
  )
  .then((respBlob) => {
    download(respBlob.blob, respBlob.fileName || defaultFileName, 'application/pdf');
    toast.success(`Download complete`, {
      type: 'success',
      position: 'top-right'
    });
  })
  .catch((err) => {
    const errorMessage = 'Failed to download report';
    logWarn(errorMessage, err);
    if (!isMissingTokenError(err)) {
      toast.error(errorMessage, {
        type: 'error',
        position: 'top-right'
      });
    }
    return;
  });
};